import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";

const STATUS = {
  approved: ["Approved", "bg-emerald-100 text-emerald-800"],
  changes_requested: ["Changes requested", "bg-amber-100 text-amber-800"],
  needs_review: ["Needs review", "bg-slate-200 text-slate-700"],
  needs_re_review: ["Needs re-review", "bg-rose-100 text-rose-800"],
};

const StatusBadge = ({ status, tid }) => {
  const [label, cls] = STATUS[status] || [status, "bg-slate-100"];
  return <span data-testid={tid} className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
};

const ReviewDialog = ({ event, onClose, onDecided }) => {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDetail(null); setNote("");
    if (event) api.get(`/btc_email/templates/review/${event}`).then(({ data }) => setDetail(data));
  }, [event]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/btc_email/templates/review/${event}`, { decision, note, fingerprint: detail.fingerprint });
      toast.success(decision === "approve" ? "Template approved" : "Changes requested");
      onDecided();
    } catch (e) {
      const code = e?.response?.data?.detail;
      toast.error(code === "note_required" ? "Add a note describing the changes" : "Could not save the decision");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-review-dialog">
        <DialogHeader><DialogTitle className="font-mono text-sm">{event}</DialogTitle></DialogHeader>
        {!detail ? <p className="text-sm text-slate-500">Loading preview…</p> : (
          <div className="space-y-4">
            <p className="text-sm"><span className="text-slate-500">Subject: </span><strong data-testid="template-review-subject">{detail.subject}</strong></p>
            <iframe title="Email preview" sandbox="" srcDoc={detail.html} data-testid="template-review-html"
              className="w-full h-[420px] border rounded bg-white" />
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600">Plain-text version</summary>
              <pre className="whitespace-pre-wrap bg-slate-50 border rounded p-3 mt-2" data-testid="template-review-text">{detail.text}</pre>
            </details>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required when requesting changes)"
              data-testid="template-review-note" />
            <div className="flex gap-2 justify-end">
              <button disabled={busy} onClick={() => decide("request_changes")} data-testid="template-review-request-changes"
                className="px-4 py-2 text-sm border rounded hover:bg-amber-50 disabled:opacity-50">Request changes</button>
              <button disabled={busy} onClick={() => decide("approve")} data-testid="template-review-approve"
                className="px-4 py-2 text-sm rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">Approve wording</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const TemplateReview = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    api.get("/btc_email/templates/review").then(({ data }) => setData(data)).catch(() => setData({ items: [], counts: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-slate-500">Loading templates…</p>;
  return (
    <div className="space-y-4" data-testid="template-review">
      <p className="text-sm text-slate-600 max-w-3xl">
        Previews use synthetic data only. In production, an email is sent only when its current wording is approved;
        any wording change returns the template to “Needs re-review”.
      </p>
      <div className="flex flex-wrap gap-2 text-sm" data-testid="template-review-counts">
        {Object.entries(data.counts).map(([k, v]) => <span key={k} className="px-3 py-1 rounded bg-white border">{(STATUS[k] || [k])[0]}: {v}</span>)}
      </div>
      <div className="overflow-x-auto border rounded bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>{["Category", "Email", "Subject", "Status", "Reviewed by", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={i.event} className="border-t" data-testid={`template-row-${i.event}`}>
                <td className="px-3 py-2">{i.category}</td>
                <td className="px-3 py-2"><div className="font-medium">{i.public_name}</div><div className="font-mono text-slate-500">{i.event}</div></td>
                <td className="px-3 py-2">{i.subject}</td>
                <td className="px-3 py-2"><StatusBadge status={i.status} tid={`template-status-${i.event}`} /></td>
                <td className="px-3 py-2">{i.reviewer_name ? `${i.reviewer_name} · ${new Date(i.decided_at).toLocaleDateString()}` : "—"}</td>
                <td className="px-3 py-2"><button onClick={() => setOpen(i.event)} data-testid={`template-review-open-${i.event}`}
                  className="px-3 py-1 border rounded hover:bg-slate-50">Review</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReviewDialog event={open} onClose={() => setOpen(null)} onDecided={() => { setOpen(null); load(); }} />
    </div>
  );
};
